import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  Edit2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { CheckboxFieldset } from "ui/checkboxFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Label } from "ui/label";
import { Checkbox } from "ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { usePageHeader } from "contexts/PageHeaderContext";
import { getVehicleTypes, VehicleType } from "services/vehicleTypeMaster";
import {
  searchVendors,
  getVendor,
  getActiveVenues,
  createVendor,
  updateVendor,
  Venue,
} from "services/vendorMasterService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";

type Vendor = {
  id: string;
  code: string;
  name: string;
  contact: string;
  phone: string;
  contacts?: VendorContact[];
  vehicleTypes: string;
  areas: string;
  priority: string;
  status: string;
};

type VendorContact = {
  publicId?: string;
  name: string;
  department: string;
  phone: string;
  email: string;
};

export function VendorMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [form, setForm] = useState<Partial<Vendor>>({});
  const [contacts, setContacts] = useState<VendorContact[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  const [errors, setErrors] = useState({
    name: "",
    priority: "",
    vehicleTypes: "",
    areas: "",
  });

  const [contactErrors, setContactErrors] = useState<
    Record<
      number,
      {
        name: string;
        department: string;
        phone: string;
        email: string;
      }
    >
  >({});

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-4 w-4" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    let active = true;

    const loadVehicleTypes = async () => {
      try {
        const data = await getVehicleTypes();

        if (active) {
          setVehicleTypes(data);
        }
      } catch (error) {
        console.error("Failed to load vehicle types:", error);

        if (active) {
          toast.error("Failed to load vehicle types");
        }
      }
    };

    loadVehicleTypes();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadVenues = async () => {
      try {
        const data = await getActiveVenues();

        if (active) {
          setVenueOptions(data);
        }
      } catch (error) {
        console.error("Failed to load venues:", error);

        if (active) {
          setVenueOptions([]);
          toast.error("Failed to load venues");
        }
      }
    };

    loadVenues();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPageNo(1);
  }, [search]);

  useEffect(() => {
    let active = true;

    const loadVendors = async () => {
      try {
        setLoading(true);

        const searchValue = search.trim();

        const normalizedSearch = searchValue.toLowerCase();

        const matchedVehicleType = vehicleTypes.find(
          (item) => item.typeName?.trim().toLowerCase() === normalizedSearch,
        );

        const matchedVenue = venueOptions.find(
          (item) =>
            item.venueName?.trim().toLowerCase() === searchValue.toLowerCase(),
        );

        const priorityValues = ["Primary", "Secondary", "Backup"];

        const matchedPriority = priorityValues.find(
          (priority) => priority.toLowerCase() === searchValue.toLowerCase(),
        );

        const response = await searchVendors({
          Name:
            searchValue &&
            !matchedVehicleType &&
            !matchedVenue &&
            !matchedPriority
              ? searchValue
              : undefined,

          Priority: matchedPriority || undefined,

          VehicleTypePublicId: matchedVehicleType?.publicId || undefined,

          VenuePublicId: matchedVenue?.publicId || undefined,

          PageNumber: pageNo,
          PageSize: pageSize,
        });

        if (!active) return;

        if (!response.success) {
          setVendors([]);
          setTotalItems(0);

          toast.error(
            response.message ||
              (response.errors?.length
                ? response.errors.join(", ")
                : "Failed to load vendors."),
          );

          return;
        }

        let filteredData = response.data;

        if (matchedVehicleType) {
          filteredData = response.data.filter((item) =>
            item.vendorVehicleTypes?.some(
              (vehicleType) =>
                vehicleType.publicId === matchedVehicleType.publicId,
            ),
          );
        }

        if (matchedVenue) {
          filteredData = filteredData.filter((item) =>
            item.venues?.some(
              (venue) => venue.publicId === matchedVenue.publicId,
            ),
          );
        }

        const mappedVendors: Vendor[] = filteredData.map((item) => ({
          id: item.publicId,
          code: item.code || "",
          name: item.name,
          contact: item.contacts?.[0]?.name || "",
          phone: item.contacts?.[0]?.contactNumber || "",

          contacts:
            item.contacts?.map((contact) => ({
              publicId: contact.publicId,
              name: contact.name,
              department: contact.department || "",
              phone: contact.contactNumber,
              email: contact.email,
            })) || [],

          vehicleTypes:
            item.vendorVehicleTypes
              ?.map((vehicleType) => vehicleType.name)
              .join(", ") || "",

          areas: item.venues?.map((venue) => venue.name).join(", ") || "",

          priority: item.priority || "",
          status: item.isActive ? "Active" : "Inactive",
        }));

        setVendors(mappedVendors);

        setTotalItems(
          matchedVehicleType || matchedVenue
            ? filteredData.length
            : response.totalRecords,
        );
      } catch (error: unknown) {
        console.error("Failed to load vendors:", error);

        if (active) {
          setVendors([]);
          setTotalItems(0);

          const message = getErrorMessage(error, "Failed to load vendor.");

          toast.error(message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadVendors();

    return () => {
      active = false;
    };
  }, [search, pageNo, pageSize, vehicleTypes, venueOptions]);

  const selectedValues = (value?: string) =>
    value
      ?.split(",")
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const toggleSelection = (field: "vehicleTypes" | "areas", value: string) => {
    const selected = selectedValues(form[field]);
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    setForm((current) => ({ ...current, [field]: next.join(", ") }));
  };

  const showForm = async (vendor?: Vendor) => {
    if (!vendor) {
      setEditing(null);
      setForm({
        priority: "",
        status: "Active",
      });

      setContacts([
        {
          name: "",
          department: "",
          phone: "",
          email: "",
        },
      ]);

      setErrors({
        name: "",
        priority: "",
        vehicleTypes: "",
        areas: "",
      });

      setContactErrors({});
      setOpen(true);
      return;
    }

    try {
      setLoading(true);

      const response = await getVendor(vendor.id);

      if (!response.success) {
        toast.error(
          response.message ||
            (response.errors?.length
              ? response.errors.join(", ")
              : "Failed to load vendor."),
        );
        return;
      }

      const item = response.data;

      setEditing(vendor);

      setForm({
        id: item.publicId,
        code: item.code || vendor.code || "",
        name: item.name,
        priority: item.priority,
        vehicleTypes:
          item.vendorVehicleTypes
            ?.map((vehicleType) => vehicleType.name)
            .join(", ") || "",

        areas: item.venues?.map((venue) => venue.name).join(", ") || "",
        status: item.isActive ? "Active" : "Inactive",
      });

      setContacts(
        item.contacts?.length
          ? item.contacts.map((contact) => ({
              id: contact.publicId,
              publicId: contact.publicId,
              name: contact.name,
              department: contact.department || "",
              phone: contact.contactNumber,
              email: contact.email,
            }))
          : [
              {
                name: "",
                department: "",
                phone: "",
                email: "",
              },
            ],
      );

      setErrors({
        name: "",
        priority: "",
        vehicleTypes: "",
        areas: "",
      });

      setContactErrors({});
      setOpen(true);
    } catch (error: unknown) {
      console.error("Failed to load vendor:", error);

      const message = getErrorMessage(error, "Failed to load vendor.");

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const VENDOR_NAME_REGEX = /^[A-Za-z ]{1,30}$/;
  const CONTACT_PERSON_REGEX = /^[A-Za-z ]{1,30}$/;
  const PHONE_REGEX = /^\+?[0-9 ]{1,15}$/;
  const EMAIL_REGEX = /^[A-Za-z0-9]+@[A-Za-z0-9]+\.[A-Za-z]{2,}$/;

  const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "object" && error !== null && "response" in error) {
      const response = (
        error as {
          response?: {
            data?: {
              message?: unknown;
              errors?: unknown;
            };
          };
        }
      ).response;

      const message = response?.data?.message;

      if (typeof message === "string" && message.trim()) {
        return message;
      }

      const errors = response?.data?.errors;

      if (Array.isArray(errors)) {
        const messages = errors.filter(
          (item): item is string => typeof item === "string",
        );

        if (messages.length > 0) {
          return messages.join(", ");
        }
      }
    }

    return fallback;
  };

  const updateContact = (
    index: number,
    field: "name" | "department" | "phone" | "email",
    value: string,
  ) => {
    setContacts((current) =>
      current.map((contact, contactIndex) =>
        contactIndex === index
          ? { ...contact, [field]: value }
          : contact,
      ),
    );

    setContactErrors((current) => {
      const currentErrors = current[index] || {
        name: "",
        department: "",
        phone: "",
        email: "",
      };

      let error = "";

      if (field === "name") {
        if (!value.trim()) {
          error = "Contact Person is required";
        } else if (!CONTACT_PERSON_REGEX.test(value.trim())) {
          error =
            "Contact Person can contain only letters and spaces (maximum 30 characters).";
        }
      }

      if (field === "department") {
        if (!value.trim()) {
          error = "Department is required";
        } else if (!CONTACT_PERSON_REGEX.test(value.trim())) {
          error =
            "Department can contain only letters and spaces (maximum 30 characters).";
        }
      }

      if (field === "phone") {
        if (!value.trim()) {
          error = "Phone Number is required";
        } else if (!PHONE_REGEX.test(value.trim())) {
          error =
            "Phone Number can contain only numbers, spaces, and + at the beginning (maximum 15 characters).";
        }
      }

      if (field === "email") {
        if (!value.trim()) {
          error = "Email is required";
        } else if (!EMAIL_REGEX.test(value.trim())) {
          error =
            "Enter a valid email address. Only letters, numbers, @ and . are allowed.";
        }
      }

      return {
        ...current,
        [index]: {
          ...currentErrors,
          [field]: error,
        },
      };
    });
  };

  const save = async () => {
    const newErrors = {
      name: "",
      contact: "",
      phone: "",
      priority: "",
      vehicleTypes: "",
      areas: "",
    };

    // Vendor Name
    if (!form.name?.trim()) {
      newErrors.name = "Vendor Name is required";
    } else if (!VENDOR_NAME_REGEX.test(form.name.trim())) {
      newErrors.name =
        "Vendor Name can contain only letters and spaces (maximum 30 characters).";
    }

    // Priority
    if (!form.priority?.trim()) {
      newErrors.priority = "Priority is required";
    }

    // Vehicle Types
    if (!form.vehicleTypes?.trim()) {
      newErrors.vehicleTypes = "At least one Vehicle type must be selected";
    }

    // Operating Venues
    if (!form.areas?.trim()) {
      newErrors.areas = "At least one Operating venue must be selected";
    }

    setErrors(newErrors);

    const newContactErrors: Record<
      number,
      {
        name: string;
        department: string;
        phone: string;
        email: string;
      }
    > = {};

    contacts.forEach((contact, index) => {
      const contactError = {
        name: "",
        department: "",
        phone: "",
        email: "",
      };

      if (!contact.name.trim()) {
        contactError.name = "Contact Person is required";
      } else if (!CONTACT_PERSON_REGEX.test(contact.name.trim())) {
        contactError.name =
          "Contact Person can contain only letters and spaces (maximum 30 characters).";
      }

      if (!contact.department.trim()) {
        contactError.department = "Department is required";
      } else if (!CONTACT_PERSON_REGEX.test(contact.department.trim())) {
        contactError.department =
          "Department can contain only letters and spaces (maximum 30 characters).";
      }

      if (!contact.phone.trim()) {
        contactError.phone = "Phone Number is required";
      } else if (!PHONE_REGEX.test(contact.phone.trim())) {
        contactError.phone =
          "Phone Number can contain only numbers, spaces, and + at the beginning (maximum 15 characters).";
      }

      if (!contact.email.trim()) {
        contactError.email = "Email is required";
      } else if (!EMAIL_REGEX.test(contact.email.trim())) {
        contactError.email =
          "Enter a valid email address. Only letters, numbers, @ and . are allowed.";
      }

      if (
        contactError.name ||
        contactError.department ||
        contactError.phone ||
        contactError.email
      ) {
        newContactErrors[index] = contactError;
      }
    });

    setContactErrors(newContactErrors);

    const hasContactErrors = Object.keys(newContactErrors).length > 0;

    setErrors(newErrors);

    if (
      newErrors.name ||
      newErrors.priority ||
      newErrors.vehicleTypes ||
      newErrors.areas ||
      hasContactErrors ||
      contacts.length === 0
    ) {
      return;
    }

    const name = form.name?.trim() || "";
    const priority = form.priority?.trim() || "";

    const validContacts = contacts.map((contact) => ({
      ...contact,
      name: contact.name.trim(),
      department: contact.department.trim(),
      phone: contact.phone.trim(),
      email: contact.email.trim(),
    }));

    const selectedVehicleTypeNames = selectedValues(form.vehicleTypes);

    const selectedVehicleTypePublicIds = vehicleTypes
      .filter((item) => selectedVehicleTypeNames.includes(item.typeName))
      .map((item) => item.publicId);

    const selectedVenueNames = selectedValues(form.areas);

    const selectedVenuePublicIds = venueOptions
      .filter((venue) => selectedVenueNames.includes(venue.venueName))
      .map((venue) => venue.publicId);

    if (selectedVehicleTypePublicIds.length === 0) {
      toast.error("Please select at least one valid vehicle type.");
      return;
    }

    if (selectedVenuePublicIds.length === 0) {
      toast.error("Please select at least one valid operating venue.");
      return;
    }

    try {
      const response = editing
        ? await updateVendor(editing.id, {
            name,
            priority,
            isActive: form.status === "Active",
            vehicleTypePublicIds: selectedVehicleTypePublicIds,
            venuePublicIds: selectedVenuePublicIds,
            contacts: validContacts.map((contact) => ({
              ...(contact.publicId ? { publicId: contact.publicId } : {}),
              name: contact.name,
              department: contact.department,
              contactNumber: contact.phone,
              email: contact.email,
            })),
          })
        : await createVendor({
            name,
            priority,
            isActive: form.status === "Active",
            vehicleTypePublicIds: selectedVehicleTypePublicIds,
            venuePublicIds: selectedVenuePublicIds,
            contacts: validContacts.map((contact) => ({
              name: contact.name,
              department: contact.department,
              contactNumber: contact.phone,
              email: contact.email,
            })),
          });

      if (!response.success) {
        toast.error(
          response.message ||
            (response.errors?.length
              ? response.errors.join(", ")
              : editing
                ? "Failed to update vendor."
                : "Failed to create vendor."),
        );
        return;
      }

      toast.success(
        response.message ||
          (editing
            ? "Vendor updated successfully."
            : "Vendor created successfully."),
      );

      setOpen(false);

      setErrors({
        name: "",
        priority: "",
        vehicleTypes: "",
        areas: "",
      });

      setContactErrors({});
      setLoading(true);

      try {
        const refreshedResponse = await searchVendors({
          Name: search.trim() || undefined,
          PageNumber: pageNo,
          PageSize: pageSize,
        });

        if (refreshedResponse.success) {
          const mappedVendors: Vendor[] = refreshedResponse.data.map(
            (item) => ({
              id: item.publicId,
              code: item.code || "",
              name: item.name,
              contact: item.contacts?.[0]?.name || "",
              phone: item.contacts?.[0]?.contactNumber || "",
              contacts:
                item.contacts?.map((contact) => ({
                  id: contact.publicId,
                  publicId: contact.publicId,
                  name: contact.name,
                  department: contact.department || "",
                  phone: contact.contactNumber,
                  email: contact.email,
                })) || [],
              vehicleTypes:
                item.vendorVehicleTypes
                  ?.map((vehicleType) => vehicleType.name)
                  .join(", ") || "",

              areas: item.venues?.map((venue) => venue.name).join(", ") || "",
              priority: item.priority || "",
              status: item.isActive ? "Active" : "Inactive",
            }),
          );

          setVendors(mappedVendors);
          setTotalItems(refreshedResponse.totalRecords);
        } else {
          toast.error(
            refreshedResponse.message ||
              (refreshedResponse.errors?.length
                ? refreshedResponse.errors.join(", ")
                : "Failed to refresh vendors."),
          );
        }
      } catch (error: unknown) {
        console.error("Failed to refresh vendors:", error);

        const message = getErrorMessage(error, "Failed to load vendors.");

        toast.error(message);
      } finally {
        setLoading(false);
      }
    } catch (error: unknown) {
      console.error("Failed to create vendor:", error);

      const message = getErrorMessage(
        error,
        editing ? "Failed to update vendor." : "Failed to create vendor.",
      );

      toast.error(message);
    }
  };

  const sortedVendors = [...vendors].sort((a, b) => {
    if (!sortColumn) {
      return 0;
    }

    let aValue = "";
    let bValue = "";

    switch (sortColumn) {
      case "name":
        aValue = a.name || "";
        bValue = b.name || "";
        break;

      case "contact":
        aValue = a.contact || "";
        bValue = b.contact || "";
        break;

      case "vehicleTypes":
        aValue = a.vehicleTypes || "";
        bValue = b.vehicleTypes || "";
        break;

      case "areas":
        aValue = a.areas || "";
        bValue = b.areas || "";
        break;

      case "priority":
        aValue = a.priority || "";
        bValue = b.priority || "";
        break;

      default:
        return 0;
    }

    const comparison = aValue.localeCompare(bValue, undefined, {
      sensitivity: "base",
    });

    return sortDirection === "asc" ? comparison : -comparison;
  });

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="h-10 pl-9 pr-9 shadow-sm"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by vendor, vehicle type or venue name"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              className="bg-[#5B9BD5] text-white"
              onClick={() => showForm()}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Vendor
            </Button>
          </div>
        </div>

        <Card
          className="border-[#5B9BD5]/30 bg-white"
          style={{ gap: 0, marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#E3F2FD]">
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Vendor
                        {sortColumn === "name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("contact")}
                    >
                      <div className="flex items-center gap-1">
                        Contact
                        {sortColumn === "contact" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("vehicleTypes")}
                    >
                      <div className="flex items-center gap-1">
                        Vehicle Types
                        {sortColumn === "vehicleTypes" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("areas")}
                    >
                      <div className="flex items-center gap-1">
                        Operating Venues
                        {sortColumn === "areas" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("priority")}
                    >
                      <div className="flex items-center gap-1">
                        Priority
                        {sortColumn === "priority" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Status
                    </TableHead>
                    <TableHead className="px-4 py-3 text-right text-xs text-[#5B9BD5]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVendors.map((vendor) => (
                    <TableRow key={vendor.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-100">
                            <Building2 className="h-4 w-4 text-[#5B9BD5]" />
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-700">
                              {vendor.name}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {vendor.code}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-700">
                        {vendor.contact}
                        <div className="text-[11px] text-gray-400">
                          {vendor.phone}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex max-w-xs flex-wrap gap-1">
                          {selectedValues(vendor.vehicleTypes).map((value) => (
                            <Badge
                              key={value}
                              variant="outline"
                              className="text-[10px] font-normal"
                            >
                              {value}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex max-w-xs flex-wrap gap-1">
                          {selectedValues(vendor.areas).map((value) => (
                            <Badge
                              key={value}
                              variant="outline"
                              className="text-[10px] font-normal"
                            >
                              {value}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="text-[10px] font-normal"
                        >
                          {vendor.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <Badge
                          variant="outline"
                          className={`${
                            vendor.status === "Active"
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          } text-xs`}
                        >
                          {vendor.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => showForm(vendor)}
                          className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                          aria-label={`Edit ${vendor.name}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!vendors.length && (
                    <TableRow> 
                      <TableCell
                        colSpan={7}
                        className="h-28 text-center text-sm text-gray-500"
                      >
                        No vendors found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {vendors.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No vendors found</p>
                </div>
              ) : (
                vendors.map((vendor) => (
                  <Card
                    key={vendor.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent style={{ padding: "10px" }}>
                      {/* Vendor header */}
                      <div className="flex items-start justify-between gap-3">
                        {/* Vendor information */}
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-[#5B9BD5]" />
                          </div>

                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {vendor.name}
                            </p>

                            <p className="text-xs text-[#5B9BD5]">
                              {vendor.code}
                            </p>
                          </div>
                        </div>

                        {/* Status + Edit */}
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge
                            variant="outline"
                            className={
                              vendor.status === "Active"
                                ? "bg-green-100 text-green-700 border-green-200 text-xs"
                                : "bg-gray-100 text-gray-700 border-gray-200 text-xs"
                            }
                          >
                            {vendor.status}
                          </Badge>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => showForm(vendor)}
                            aria-label={`Edit ${vendor.name}`}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                          </Button>
                        </div>
                      </div>

                      {/* Vendor details */}
                      <div className="mt-3 space-y-1">
                        <div className="font-semibold text-gray-900 text-sm">
                          Contact:{" "}
                          <span className="text-xs text-gray-500">
                            {vendor.contact}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center justify-between text-sm text-gray-500">
                          <div className="font-semibold text-gray-900 text-sm">
                            Phone:{" "}
                            <span className="text-xs text-gray-500">
                              {vendor.phone}
                            </span>
                          </div>
                          <div className="font-semibold text-gray-900 text-sm">
                            Priority:{" "}
                            <span className="text-xs text-gray-500">
                              {vendor.priority}
                            </span>
                          </div>
                        </div>

                        <div className="font-semibold text-gray-900 text-sm">
                          Vehicle Types:
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedValues(vendor.vehicleTypes).map(
                              (value) => (
                                <Badge
                                  key={value}
                                  variant="outline"
                                  className="text-[10px] font-normal"
                                >
                                  {value}
                                </Badge>
                              ),
                            )}
                          </div>
                        </div>

                        <div className="font-semibold text-gray-900 text-sm">
                          Operating Venues:
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedValues(vendor.areas).map((value) => (
                              <Badge
                                key={value}
                                variant="outline"
                                className="text-[10px] font-normal"
                              >
                                {value}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={totalItems}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="vendors"
          />
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="!w-[calc(100vw-1rem)] sm:!w-[70vw] !max-w-[1200px] !max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle>
                {editing ? "Edit Vendor" : "Add Vendor"}
              </DialogTitle>
              <DialogDescription>
                Maintain vendor contact details and select capabilities from the
                configured masters.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2 sm:grid-cols-2">
              <div className="space-y-2">
                <InputFieldset
                  label="Vendor Name"
                  required
                  value={form.name || ""}
                  placeholder="Enter vendor name"
                  maxLength={30}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (!/^[A-Za-z ]*$/.test(value)) {
                      return;
                    }

                    if (value.length > 30) {
                      return;
                    }

                    setForm({
                      ...form,
                      name: value,
                    });

                    setErrors({
                      ...errors,
                      name: "",
                    });
                  }}
                  className={`text-sm ${
                    errors.name ? "border-red-500 focus:ring-red-500" : ""
                  }`}
                />

                {errors.name && (
                  <p className="text-red-500 text-xs">{errors.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Priority" required>
                  <Select
                    value={form.priority || ""}
                    onValueChange={(value) => {
                      setForm({
                        ...form,
                        priority: value,
                      });

                      setErrors({
                        ...errors,
                        priority: "",
                      });
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue placeholder="Select priority" />
                    </SelectTriggerUI>

                    <SelectContent>
                      <SelectItem value="Primary">Primary</SelectItem>
                      <SelectItem value="Secondary">Secondary</SelectItem>
                      <SelectItem value="Backup">Backup</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {errors.priority && (
                  <p className="text-red-500 text-xs">{errors.priority}</p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-700">
                    Contacts <span className="text-red-500">*</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8"
                    style={{ background: "#5B9BD5" }}
                    title="Add another contact"
                    aria-label="Add another contact"
                    onClick={() =>
                      setContacts((current) => [
                        ...current,
                        {
                          name: "",
                          department: "",
                          phone: "",
                          email: "",
                        },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Contact 
                  </Button>
                </div>
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  {contacts.map((contact, index) => (
                    <div
                      key={contact.publicId ?? index}
                      className="grid items-start gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_32px]"
                    >
                      <div className="space-y-1">
                        <InputFieldset
                          label="Name"
                          required
                          value={contact.name}
                          placeholder="Contact person name"
                          onChange={(e) => {
                            const value = e.target.value;

                            if (!/^[A-Za-z ]*$/.test(value)) {
                              return;
                            }

                            if (value.length > 30) {
                              return;
                            }

                            updateContact(index, "name", value);
                          }}
                          className={
                            contactErrors[index]?.name
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }
                        />

                        {contactErrors[index]?.name && (
                          <p className="text-red-500 text-xs">
                            {contactErrors[index].name}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <InputFieldset
                          label="Department"
                          required
                          value={contact.department}
                          placeholder="Enter department"
                          onChange={(e) => {
                            const value = e.target.value;

                            if (!/^[A-Za-z ]*$/.test(value)) {
                              return;
                            }

                            if (value.length > 30) {
                              return;
                            }

                            updateContact(index, "department", value);
                          }}
                          className={
                            contactErrors[index]?.department
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }
                        />

                        {contactErrors[index]?.department && (
                          <p className="text-red-500 text-xs">
                            {contactErrors[index].department}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <InputFieldset
                          label="Phone Number"
                          required
                          placeholder="Enter phone number"
                          value={contact.phone}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (!/^\+?[0-9 ]*$/.test(value)) {
                              return;
                            }

                            if (value.length > 15) {
                              return;
                            }

                            updateContact(index, "phone", value);
                          }}
                          className={
                            contactErrors[index]?.phone
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }
                        />

                        {contactErrors[index]?.phone && (
                          <p className="text-red-500 text-xs">
                            {contactErrors[index].phone}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <InputFieldset
                          label="Email"
                          required
                          type="email"
                          placeholder="Enter email"
                          value={contact.email}
                          onChange={(e) =>
                            updateContact(index, "email", e.target.value)
                          }
                          className={
                            contactErrors[index]?.email
                              ? "border-red-500 focus:ring-red-500"
                              : ""
                          }
                        />

                        {contactErrors[index]?.email && (
                          <p className="text-red-500 text-xs">
                            {contactErrors[index].email}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={contacts.length === 1}
                        onClick={() => {
                          setContacts((current) =>
                            current.filter((_, contactIndex) => contactIndex !== index),
                          );

                          setContactErrors((current) => {
                            const next = { ...current };
                            delete next[index];
                            return next;
                          });
                        }}
                        aria-label={`Remove contact ${index + 1}`}
                        className="mt-5 flex h-8 w-8 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-red-600 shadow-none hover:bg-transparent hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <CheckboxFieldset label="Vehicle Types" required>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border rounded-lg bg-gray-50">
                    {vehicleTypes
                      .filter((item) => item.isActive)
                      .map((item) => {
                        const selected = selectedValues(
                          form.vehicleTypes,
                        ).includes(item.typeName);

                        return (
                          <div
                            key={item.publicId}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={`vehicle-type-${item.publicId}`}
                              checked={selected}
                              onCheckedChange={() => {
                                toggleSelection("vehicleTypes", item.typeName);

                                setErrors((prev) => ({
                                  ...prev,
                                  vehicleTypes: "",
                                }));
                              }}
                              className="h-4 w-4"
                            />

                            <Label
                              htmlFor={`vehicle-type-${item.publicId}`}
                              className="cursor-pointer text-sm font-normal"
                            >
                              {item.typeName}
                            </Label>
                          </div>
                        );
                      })}
                  </div>
                </CheckboxFieldset>

                {errors.vehicleTypes && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.vehicleTypes}
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <CheckboxFieldset label="Operating Venues" required>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border rounded-lg bg-gray-50">
                    {venueOptions.map((venue) => {
                      const selected = selectedValues(form.areas).includes(
                        venue.venueName,
                      );

                      return (
                        <div
                          key={venue.publicId}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`venue-${venue}`}
                            checked={selected}
                            onCheckedChange={() => {
                              toggleSelection("areas", venue.venueName);

                              setErrors((prev) => ({
                                ...prev,
                                areas: "",
                              }));
                            }}
                            className="h-4 w-4"
                          />

                          <Label
                            htmlFor={`venue-${venue}`}
                            className="cursor-pointer text-sm font-normal"
                          >
                            {venue.venueName}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </CheckboxFieldset>

                {errors.areas && (
                  <p className="text-red-500 text-xs mt-1">{errors.areas}</p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <CheckboxFieldset label="Status">
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
                    <Checkbox
                      id="active-vendor"
                      checked={form.status === "Active"}
                      onCheckedChange={(checked) => {
                        setForm((current) => ({
                          ...current,
                          status: checked ? "Active" : "Inactive",
                        }));
                      }}
                      className="h-5 w-5"
                    />

                    <Label
                      htmlFor="active-vendor"
                      className="cursor-pointer text-sm font-normal"
                    >
                      Active vendor
                    </Label>
                  </div>
                </CheckboxFieldset>
              </div>
            </div>
            <DialogFooter className="mt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  setErrors({
                    name: "",
                    priority: "",
                    vehicleTypes: "",
                    areas: "",
                  });
                  setContactErrors({});
                }}
              >
                Cancel
              </Button>
              <Button className="bg-[#5B9BD5] text-white" onClick={save}>
                {editing ? "Update Vendor" : "Save Vendor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
